import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Dayjs } from "dayjs";
import {
  buildDateRangeSearch,
  isDateRangeInvalid,
  resolveInitialDateRange,
} from "./dateRangeParams";

/**
 * The date range of a send list. Normally arrived at from the dashboard with the range already
 * in the URL; a direct link with no params opens on the same current-month default the
 * dashboard uses.
 *
 * The pages own date pickers of their own, so the active range is mirrored back into the URL.
 * Without it a refresh would silently revert to whatever range the dashboard linked in, showing
 * a different set of documents than the one on screen.
 */
export function useSendDateRange() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRange = useRef(
    resolveInitialDateRange(
      searchParams.get("date_from"),
      searchParams.get("date_to"),
    ),
  ).current;
  const [startDate, setStartDate] = useState<Dayjs>(initialRange.from);
  const [endDate, setEndDate] = useState<Dayjs>(initialRange.to);

  const changeDateRange = (start: Dayjs, end: Dayjs) => {
    setStartDate(start);
    setEndDate(end);
  };

  const dateSearch = buildDateRangeSearch(startDate, endDate);

  useEffect(() => {
    const current = `?${searchParams.toString()}`;
    if (current !== (dateSearch || "?")) {
      setSearchParams(dateSearch ? dateSearch.slice(1) : "", { replace: true });
    }
  }, [dateSearch, searchParams, setSearchParams]);

  return {
    startDate,
    endDate,
    changeDateRange,
    dateSearch,
    isRangeInvalid: isDateRangeInvalid(startDate, endDate),
  };
}
